// eslint-disable-next-line import/no-unresolved
import { Bridge } from '@instant-messengers/vk-teams-bridge';
import { fetchActToken } from '@mail/offline-kit/sso/act-token-fetcher';
import { configureSsoAuthRedirect } from '@mail/offline-kit/sso/configureSsoAuthRedirect';
import { RetryTracker } from '@mail/offline-kit/sso/RetryTracker';
import { runSilentAuthFlow, shouldRedirectToLogin } from '@mail/offline-kit/sso/runVktSilentAuthFlow';
import { parse } from 'qs';
import { DOMAIN_HOST, IS_ANDROID_MOBILE, IS_IOS_MOBILE, IS_VKT_DESKTOP } from 'reactApp/appHelpers/configHelpers';
import { VK_UI_DARK_THEME } from 'reactApp/constants';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { safetyLocalStorage } from 'reactApp/services/localStorage/safetyLocalStorage';
import { networkStatus } from 'reactApp/services/networkStatus';
import { loginShow } from 'reactApp/utils/auth';
import { captureException } from 'reactApp/utils/tracer';

type VKTColorScheme = { colorsScheme: 'light' | 'dark' };

export const vkTeamsBridge = new Bridge();

type VKTeamsOptions = { staticPage?: boolean };
type VKTeamsAppOptions = VKTeamsOptions & { isReady: boolean };

const VKTeamsMessages = {
    LoadingCompleted: 'LoadingCompleted',
    GetThemeSettings: 'GetThemeSettings',
    GetAuth: 'GetAuth',
} as const;

// TODO: См. sendVKTeamsMessageWithReliableAnswer
// const WAIT_RESPONSE_FROM_VKT_TIMEOUT = 1000;

const SSO_BRIDGE_TIMEOUT = 15000;
const MAX_SSO_FAILS_WARNING = 10;
const SSO_BACKOFF_INC = 1500;
const SSO_MAX_BACKOFF_MS = 30000;

function preventTransitionEffects(): () => void {
    document.body.classList.add('notransition');
    return () => {
        const _ = window.getComputedStyle(document.body).opacity;
        document.body.classList.remove('notransition');
    };
}

function sendVKTeamsMessageWithReliableAnswer<T>(message: keyof typeof VKTeamsMessages, args: Record<string, unknown>): Promise<T> {
    return Promise.race<T>([
        vkTeamsBridge.send(message, args),
        /**
         * TODO:
         * Вернуть после резолва таски https://jira.vk.team/browse/B2BCLOUD-1032
         * Вотчдог сейчас срабатывает раньше, чем ВКТ успевает отправить или
         * ответить нашему приложение, Решили пока ждать ответа максимально долго
         * или не загружать приложение
         */
        // new Promise((_resolve, reject) => setTimeout(() => reject(new Error('VKTeams not answering')), WAIT_RESPONSE_FROM_VKT_TIMEOUT)),
    ]);
}

const logToConsole = (opts: {
    name: string;
    params?: Record<string, string | number | Error | unknown> & {
        logLevel?: 'info' | 'error';
    };
}) => {
    try {
        const params = opts.params ?? {};
        const logLevel = params.logLevel === 'error' ? 'error' : 'info';

        const payload = logLevel === 'error' ? { ...params, stack: new Error('stack').stack } : params;

        // eslint-disable-next-line no-console
        (logLevel === 'error' ? console.error : console.warn)(`[vkt-sso] ${opts.name}`, JSON.stringify(payload));
    } catch (_) {}
};

export class VKTeamsClient {
    private static lastCall: Promise<unknown> = Promise.resolve();

    private static authPromise: Promise<unknown> | null = null;

    private constructor() {
        /* Decline manual creation  */
    }

    static runInsideVKTeams() {
        const urlSearchParams = parse(window.location.search, { ignoreQueryPrefix: true });
        return window?.cloudSettings?.params?.hasOwnProperty('ERROR_PAGE_STATUS') && urlSearchParams?.wv === '1';
    }

    static changeThemeSubscribe() {
        vkTeamsBridge.subscribe('ThemeSettings', (data) => {
            const body = document.body;
            const hasDarkClass = body.classList.contains(VK_UI_DARK_THEME);

            if ((data.colorsScheme === 'dark' || data.colorScheme === 'dark') && !hasDarkClass) {
                body.classList.add(VK_UI_DARK_THEME);
            } else if ((data.colorsScheme === 'light' || data.colorScheme === 'light') && hasDarkClass) {
                body.classList.remove(VK_UI_DARK_THEME);
            }
        });
    }

    static applyAppThemeSettings(options: VKTeamsOptions = {}) {
        if (options.staticPage && !VKTeamsClient.runInsideVKTeams()) {
            return VKTeamsClient;
        }
        VKTeamsClient.lastCall = VKTeamsClient.lastCall.then(() => {
            this.changeThemeSubscribe();

            return sendVKTeamsMessageWithReliableAnswer<VKTColorScheme>(VKTeamsMessages.GetThemeSettings, {})
                .then(({ colorsScheme }) => {
                    const applyDarkTheme = () => {
                        const resumeTransitionEffects = preventTransitionEffects();
                        document.body.classList.add(VK_UI_DARK_THEME);
                        resumeTransitionEffects();
                    };

                    if (colorsScheme === 'dark') {
                        if (document.body) {
                            applyDarkTheme();
                        } else {
                            const observer = new MutationObserver((_, obs) => {
                                if (document.body) {
                                    applyDarkTheme();
                                    obs.disconnect();
                                }
                            });

                            observer.observe(document.documentElement, {
                                childList: true,
                                subtree: true,
                            });
                        }
                    }
                })
                .catch((error: Error) => {
                    console.error(error.message);
                });
        });

        return VKTeamsClient;
    }

    /**
     * Так как страницы ошибок статические, мы не можем определить контекст запуска на сервере,
     * поэтому внедряем код непосредственно во время запуска скрипта, и аффекти лого в момент
     * когда готовы показать страницу ошибок в ВКТ.
     */
    static hideVKWorkspaceLogo(options: VKTeamsOptions = {}) {
        if (options.staticPage && !VKTeamsClient.runInsideVKTeams()) {
            return VKTeamsClient;
        }

        VKTeamsClient.lastCall = VKTeamsClient.lastCall.then(() => {
            document.body?.classList.add('nologo');
        });

        return VKTeamsClient;
    }

    static hideBlockOnErrorPage(options: VKTeamsOptions = {}) {
        if (options.staticPage && !VKTeamsClient.runInsideVKTeams()) {
            return VKTeamsClient;
        }
        document.body?.classList.add('vkTeamsErrorPage');
        return VKTeamsClient;
    }

    static changeXrayConfig(options: VKTeamsOptions = {}) {
        if (options.staticPage && !VKTeamsClient.runInsideVKTeams()) {
            return VKTeamsClient;
        }
        const config = window.xray.getConfig();
        const newConfig = { ...config };
        let pParameter: string = '';
        if (IS_ANDROID_MOBILE) {
            pParameter = 'cloud-vkt-android';
        } else if (IS_IOS_MOBILE) {
            pParameter = 'cloud-vkt-ios';
        } else if (IS_VKT_DESKTOP) {
            pParameter = 'cloud-vkt-desktop';
        } else {
            pParameter = 'cloud-vkt-web';
        }
        newConfig.defaultParams.p = pParameter;
        window.xray.setConfig(newConfig);

        return VKTeamsClient;
    }

    static notifyAppReady(options: VKTeamsAppOptions) {
        if (options.staticPage && !VKTeamsClient.runInsideVKTeams()) {
            return VKTeamsClient;
        }

        VKTeamsClient.lastCall = VKTeamsClient.lastCall.then(() => {
            delete (window as any).VKTClientLoading;
            return sendVKTeamsMessageWithReliableAnswer(VKTeamsMessages.LoadingCompleted, { ok: options.isReady })
                .catch((error: Error & { reason: string }) => {
                    console.error(error.reason || error.message);
                })
                .finally(() => {
                    window.ContentLoader?.setVKTClientLoaded?.();
                });
        });

        return VKTeamsClient;
    }

    private static async getSilentToken() {
        return vkTeamsBridge.send(VKTeamsMessages.GetAuth, null);
    }

    private static async getAuthCSRFToken() {
        return fetchActToken({
            url: `https://auth.${DOMAIN_HOST}/jsapi/token`,
            responseType: 'api',
            onError: (error) => {
                captureException(error, { issueKey: 'get-auth-csrf-token' });
                VKTeamsClient.notifyAppReady({ isReady: false });
                throw new Error('Error during fetch act token');
            },
        });
    }

    private static async _refreshAuthWithSilentToken() {
        // Восстанавливаем квери параметр need_silent, если потеряли его перед заходом во флоу сквозной авторизации
        const url = new URL(window.location.href);
        if (!url.searchParams.has('need_silent')) {
            url.searchParams.set('need_silent', '1');
            window.history.pushState({}, '', url);
        }

        const ssoRedirect = configureSsoAuthRedirect({ baseUrl: `https://auth.${DOMAIN_HOST}` });
        let abortController: AbortController;
        const retryTracker = new RetryTracker(safetyLocalStorage, SSO_MAX_BACKOFF_MS);

        const runFlow = (initialRetry = retryTracker.getInitialRetry()) => {
            abortController?.abort();
            abortController = new AbortController();

            return runSilentAuthFlow({
                getSilentToken: () => VKTeamsClient.getSilentToken(),
                getAuthCSRFToken: VKTeamsClient.getAuthCSRFToken,
                networkStatus,
                initialRetryCount: initialRetry,
                maxBackoffMs: SSO_MAX_BACKOFF_MS,
                backoffIncMs: SSO_BACKOFF_INC,
                requestTimeoutMs: SSO_BRIDGE_TIMEOUT,
                signal: abortController.signal,
                sendRemoteLog: logToConsole,
                onSuccess: ({ silentToken, actToken, retryCount }) => {
                    retryTracker.commitRetry();
                    return ssoRedirect({ silentToken, actToken, retryCount });
                },
                onFail: ({ retryCount, error }) => {
                    retryTracker.commitRetry();

                    if (shouldRedirectToLogin(error) || retryCount > MAX_SSO_FAILS_WARNING) {
                        logToConsole({
                            name: 'vkt-sso-redirect-login',
                            params: {
                                error: error as Error,
                                retryCount,
                                maxRetryCount: MAX_SSO_FAILS_WARNING,
                            },
                        });
                        abortController.abort();
                        loginShow({});
                        return null;
                    }

                    if (retryCount > MAX_SSO_FAILS_WARNING) {
                        showSnackbarAction({
                            id: 'silent-auth-fail',
                            closable: true,
                            text: `Не удалось обновить данные`,
                            type: SnackbarTypes.failure,
                        });
                    }
                },
            });
        };

        return runFlow();
    }

    static async authWithSilentToken() {
        if (!VKTeamsClient.authPromise) {
            VKTeamsClient.authPromise = VKTeamsClient._refreshAuthWithSilentToken().finally(() => {
                VKTeamsClient.authPromise = null;
            });
        }

        return VKTeamsClient.authPromise;
    }
}
